import { DateTime } from 'luxon'
import { computed, Ref } from 'vue'
import { fromISOWithZone, getDateStringForAPI } from '@/libs/date/date'
import { setupDateTimeUtils } from '@/pages/PatientProfile/shared/dateAndTimezones'

export const DEFAULT_END_TIME_OFFSET_MINUTES = 120

/**
 * Shared end-time logic for the "Scheduled call end time" field
 * (behind the showScheduledStartAndEndTime flag). Owns only the end-time
 * bounds/anchoring rule (no earlier than start, no more than 5 hours after)
 * so it can't drift between callers - start-time selection (date/time
 * picker handling) stays bespoke per caller.
 * @param dueDatetime caller's current start datetime (ISO string), read-only value used to anchor its bounds
 * @param endDatetime caller's own end datetime storage (ISO string), writable
 */
export function useScheduledCallEndTime(
  dueDatetime: Ref<string | undefined>,
  endDatetime: Ref<string | null | undefined>
) {
  const { stringToLocalDateTime, getTimezoneString } = setupDateTimeUtils()

  const dirtyEndDateTime = computed<Date | null>({
    get: () => stringToLocalDateTime(endDatetime.value)?.toJSDate() ?? null,
    set: (value) => setEndDatetime(value),
  })

  const startTimeAsDate = computed(
    () => stringToLocalDateTime(dueDatetime.value)?.toJSDate() ?? null
  )

  const endTimeMinDate = computed(() => startTimeAsDate.value ?? undefined)

  const endTimeMaxDate = computed(() =>
    startTimeAsDate.value
      ? DateTime.fromJSDate(startTimeAsDate.value).plus({ hours: 5 }).toJSDate()
      : undefined
  )

  /**
   * Update the end time. Always shares dueDatetime's date - only the
   * time-of-day changes.
   *
   * Assumes the call does not cross a date boundary in the local (browser)
   * timezone - only hour/minute are updated, so a call that spans midnight
   * local time would not roll over to the next day.
   * @param value Time to set from, or null to clear
   */
  function setEndDatetime(value: Date | null) {
    if (!value) {
      endDatetime.value = undefined
      return
    }

    // End time is not required if there is no dueDatetime (start time)
    if (!dueDatetime.value) {
      return
    }

    const baseDateTime = endDatetime.value
      ? fromISOWithZone(endDatetime.value).toLocal()
      : fromISOWithZone(dueDatetime.value).toLocal()

    const newEndDateTime = baseDateTime.set({
      hour: value.getHours(),
      minute: value.getMinutes(),
    })

    endDatetime.value =
      getDateStringForAPI(newEndDateTime.toJSDate()) ?? undefined
  }

  /**
   * Reset end time to a fixed offset after a newly-selected start time,
   * overwriting any previously set end time.
   * @param newStartDateTime the just-selected start datetime
   * @param offsetMinutes minutes after start to default the end time to
   */
  function resetEndTimeToDefaultOffset(
    newStartDateTime: DateTime,
    offsetMinutes = DEFAULT_END_TIME_OFFSET_MINUTES
  ) {
    const newEndDateTime = newStartDateTime.plus({ minutes: offsetMinutes })
    endDatetime.value =
      getDateStringForAPI(newEndDateTime.toJSDate()) ?? undefined
  }

  /**
   * Clear the end time, e.g. when the start time is cleared.
   */
  function clearEndTime() {
    endDatetime.value = undefined
  }

  const scheduledCallEndTimeLabel = computed(
    () => `Scheduled call end time (Your time: ${getTimezoneString(true)})`
  )

  return {
    dirtyEndDateTime,
    endTimeMinDate,
    endTimeMaxDate,
    setEndDatetime,
    resetEndTimeToDefaultOffset,
    clearEndTime,
    scheduledCallEndTimeLabel,
  }
}
